import axios from 'axios'
import { DrawingModel } from '@/models/DrawingModel'

/**
 * 手書き入力変換(MyScript)基底クラス
 */
export default class MyScript {
  /** 変換先の言語設定 */
  protected lang: string
  /** レスポンスのMIMEタイプ */
  protected accept: string
  /** 変換先コンテンツタイプ */
  protected contentType: string
  /** 変換ステータス */
  private readonly conversionState: string
  /** 変換APIのURL */
  private readonly url: string
  /** 変換APIのアプリケーションキー */
  private readonly key: string
  /** 描画情報DPI */
  private readonly dpi: number
  /** キャンバスの幅 */
  protected canvasWidth: number
  /** キャンバスの高さ */
  protected canvasHeight: number

  /**
   * コンストラクタ
   */
  constructor() {
    // 変換先言語のデフォルト(日本語)
    this.lang = 'ja_JP'
    // 変換先コンテンツタイプのデフォルト(テキスト)
    this.contentType = 'Text'
    // 変換ステータス設定
    this.conversionState = 'DIGITAL_EDIT'
    // レスポンスタイプのデフォルト(プレーンテキスト用)
    this.accept = 'application/json, text/plain'
    // 外部API関連情報を設定ファイルから読み込む
    this.url = process.env.VUE_APP_MYSCRIPT_API_URL
    this.key = process.env.VUE_APP_MYSCRIPT_API_KEY
    // 変換DPIを設定
    this.dpi = 96
    // キャンバスのサイズに初期値を設定(API実行前に更新する)
    this.canvasWidth = 0
    this.canvasHeight = 0
  }

  /**
   * API実行時のヘッダ情報
   *
   * @returns リクエストヘッダ情報
   */
  protected get header(): MyScriptModel.RequestHeader {
    return {
      applicationKey: this.key,
      Accept: this.accept,
    }
  }

  /**
   * API実行時の共通リクエスト情報
   *
   * @param strokes ストローク情報
   * @returns リクエスト本体情報
   */
  protected defaultBody(strokes: DrawingModel.Stroke[]): MyScriptModel.RequestBody {
    return {
      contentType: this.contentType,
      conversionState: this.conversionState,
      width: this.canvasWidth,
      height: this.canvasHeight,
      xDPI: this.dpi,
      yDPI: this.dpi,
      strokeGroups: [
        {
          penStyle: null,
          strokes: this.convertStrokes(strokes),
        },
      ],
    }
  }

  /**
   * ストローク情報をMyScript用に変換する
   *
   * @param strokes ストローク情報
   * @returns MyScript用ストローク情報
   */
  private convertStrokes(strokes: DrawingModel.Stroke[]): MyScriptModel.Stroke[] {
    return strokes.map((stroke) => ({
      x: stroke.points.map((point) => point.x),
      y: stroke.points.map((point) => point.y),
    }))
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  /**
   * 変換APIを実行する
   *   変換後の値を処理するラッパーメソッド経由で実行する
   *
   * @param header リクエストヘッダ
   * @param body リクエストボディ
   * @returns 変換結果
   */
  protected async post(header: MyScriptModel.RequestHeader, body: MyScriptModel.RequestBody): Promise<any> {
    // API実行
    const response = await axios.post(this.url, body, { headers: header })

    // レスポンスが存在しない場合はnullを返す
    if (!response) {
      return null
    }

    return response.data
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

export namespace MyScriptModel {
  /**
   * MyScript用のストローク情報定義
   */
  export interface Stroke {
    /** x座標 */
    x: number[]
    /** y座標 */
    y: number[]
  }

  /**
   * API実行時のリクエストヘッダ
   */
  export interface RequestHeader {
    /** アプリケーションキー */
    applicationKey: string
    /** レスポンスのMIMEタイプ */
    Accept: string
  }

  /**
   * API実行時のリクエスト本体
   */
  export interface RequestBody {
    /** 変換先コンテンツタイプ */
    contentType: string
    /** 変換ステータス(固定値) */
    conversionState: string
    /** 描画領域の幅 */
    width: number
    /** 描画領域の高さ */
    height: number
    /** 縦方向DPI */
    xDPI: number
    /** 横方向DPI */
    yDPI: number
    /** ストローク情報 */
    strokeGroups: StrokeGroup[]
    /** 詳細設定情報 */
    configuration?: ConfigurationBase
  }

  /**
   * API実行時のストローク情報定義
   */
  interface StrokeGroup {
    /** ペン情報(null固定) */
    penStyle: null
    /** ストローク情報 */
    strokes: Stroke[]
  }

  /**
   * API実行時の詳細設定情報の基底型
   *   各変換モードでこの型を継承し、モードごとの設定情報を定義する
   */
  export type ConfigurationBase = object
}
